import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBreak } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GameState, GAME_SETTINGS, GameMode, GameStatus, loadGame } from '@/utils/gameState';
import { Copy, SendHorizontal, Star } from 'lucide-react';
import { getNormalizedDate } from '@/utils/dateUtils';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { bruteForceWord } from '@/utils/cryptoUtils';
import {
  buildPlayedDate,
  buildPuzzleDate,
  buildPuzzleId,
  trackEvent
} from '@/utils/analytics';

interface ResultModalProps {
  gameState: GameState;
  isOpen: boolean;
  onClose: () => void;
  onNewGame: (newMode: GameMode, newDate?: Date, source?: 'result_modal') => void;
  gameMode: GameMode;
  onPlayEncore: () => void;
  canPlayEncore: boolean;
  hintLetter?: string | null;
}

enum ModalStep {
  ENCORE_CONFIRM = 'encore-confirm',
  SHARE = 'share'
}

const ResultModal: React.FC<ResultModalProps> = ({
  gameState,
  isOpen,
  onClose,
  onNewGame,
  gameMode,
  onPlayEncore,
  canPlayEncore,
  hintLetter
}) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { gameStatus, targetWord, guesses } = gameState;
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<ModalStep>(ModalStep.SHARE);

  const attempts = guesses.length;
  const isWin = gameStatus === GameStatus.WIN;
  const isDailyMode = gameMode === GameMode.DAILY;
  const isEncoreMode = gameMode === GameMode.ENCORE;
  const isRandomMode = gameMode === GameMode.RANDOM;
  const isSinglePlayMode = isDailyMode || isEncoreMode;

  // Reset step when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      if (isDailyMode && canPlayEncore) {
        setStep(ModalStep.ENCORE_CONFIRM);
      } else {
        setStep(ModalStep.SHARE);
      }
    }
  }, [isOpen, isDailyMode, canPlayEncore]);

  const getMessage = () => {
    if (isWin) {
      if (isEncoreMode) return "Mambo No. 5!";
      if (attempts === 1) return "Incredible!";
      if (attempts === 2) return "Magnificent!";
      if (attempts === 3) return "Impressive!";
      if (attempts === 4) return "Good job!";
      return "You got it!";
    } else {
      return "Better luck next time!";
    }
  };

  const getDescription = () => {
    const maxAttempts = GAME_SETTINGS.maxAttempts;
    
    if (isWin) {
      if (isEncoreMode) return "You rocked it! 🌟";
      return `You guessed the word in ${attempts} ${attempts === 1 ? 'try' : 'tries'}!`;
    } else {
      const word = bruteForceWord(targetWord).toUpperCase();
      return `The word was ${word}. You used all ${maxAttempts} tries.`;
    }
  };

  const generateShareText = () => {
    const dailyGameState = loadGame(GameMode.DAILY, getNormalizedDate());
    // Always use the daily game state for sharing if we're in Encore mode
    const stateToUse = isEncoreMode && dailyGameState ? dailyGameState : gameState;
    const attemptsDisplay = stateToUse.gameStatus === 'WIN' ? stateToUse.guesses.length : 'X';
    const postfixEmoji = (isEncoreMode || isDailyMode) && hintLetter ? ' 🌟' : '';
    
    let header;
    
    if (isRandomMode) {
      // For random mode, use the target word
      const word = bruteForceWord(stateToUse.targetWord).toUpperCase();
      header = `Fourdle "${word}" ${attemptsDisplay}/${GAME_SETTINGS.maxAttempts}${postfixEmoji}`;
    } else {
      // For Daily or Archive modes, use the Fourdle number format
      header = `Fourdle ${stateToUse.targetWordNumber?.toLocaleString()} ${attemptsDisplay}/${GAME_SETTINGS.maxAttempts}${postfixEmoji}`;
    }

    // Create emoji grid
    const grid = stateToUse.evaluations
      .filter(row => row !== null)
      .map(row => {
        return row?.map(evaluation => {
          switch(evaluation) {
            case 'correct': return '🟩';
            case 'present': return '🟨';
            case 'absent': return '⬜';
            default: return '⬜';
          }
        }).join('');
      })
      .join('\n');
    
    return `${header}\n\n${grid}\n\nhttps://playfourdle.com`;
  };

  const handleShare = () => {
    const shareText = generateShareText();
    navigator.clipboard.writeText(shareText).then(() => {
      trackEvent('share', {
        method: 'copy',
        game_mode: gameMode,
        puzzle_id: buildPuzzleId(gameMode, gameState.targetWordDate),
        played_date: buildPlayedDate(),
        puzzle_date: buildPuzzleDate(gameMode, gameState.targetWordDate)
      });

      setCopied(true);
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleErrorShare = () => {
    toast({
      title: "Error sharing",
      description: "Could not share. Try copying instead!",
      variant: "destructive",
      duration: 5000
    });
  }

  const handleNativeShare = async () => {
    const shareText = generateShareText();
    
    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText
        });

        trackEvent('share', {
          method: 'native',
          game_mode: gameMode,
          puzzle_id: buildPuzzleId(gameMode, gameState.targetWordDate),
          played_date: buildPlayedDate(),
          puzzle_date: buildPuzzleDate(gameMode, gameState.targetWordDate)
        });
      } catch (err) {
        console.error('Error sharing:', err);
        handleErrorShare();
      }
    } else {
      handleErrorShare();
    }
  };

  const handlePlayAgain = useCallback(() => {
    const newMode = isSinglePlayMode ? GameMode.ARCHIVE : gameMode;
    onNewGame(newMode, undefined, 'result_modal');
  }, [gameMode, onNewGame, isSinglePlayMode]);

  // Multi-step logic -----------------------------
  // If user confirms Encore: call and close
  const handleEncoreConfirm = (answer: boolean) => {
    trackEvent('encore_prompt_decision', {
      decision: answer ? 'accept' : 'decline',
      daily_success: gameStatus === GameStatus.WIN,
      puzzle_id: buildPuzzleId(GameMode.DAILY, gameState.targetWordDate),
      played_date: buildPlayedDate(),
      puzzle_date: buildPuzzleDate(GameMode.DAILY, gameState.targetWordDate)
    });

    if (answer) {
      onPlayEncore();
      onClose();
    } else {
      setStep(ModalStep.SHARE);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            {getMessage()}
          </DialogTitle>
          <DialogDescription className="text-center text-lg">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        {step === ModalStep.ENCORE_CONFIRM && <DialogBreak className="my-0" />}

        <div className="flex flex-col space-y-4">
          {/* Show hint letter for Encore win */}
          {isEncoreMode && isWin && hintLetter && (
            <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg text-center">
              <p className="text-sm mb-2 text-muted-foreground">Here's a letter for tomorrow's Daily Fourdle:</p>
              <span className="text-3xl font-bold">{hintLetter.toUpperCase()}</span>
            </div>
          )}

          {/* STEP 1: Ask if player wants to play Encore (for daily win and canPlayEncore) */}
          {(step === ModalStep.ENCORE_CONFIRM) && isDailyMode && canPlayEncore && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-base text-center">Play Encore for a chance to get a letter for tomorrow's Fourdle?</p>
              <div className="flex gap-4 w-full">
                <Button
                  onClick={() => handleEncoreConfirm(true)}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 flex items-center justify-center gap-2"
                >
                  <Star className="h-5 w-5" />
                  Play Encore!
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleEncoreConfirm(false)}
                  className="w-full flex items-center justify-center gap-2"
                >
                  No, thanks
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: Share/Copy UI (random mode OR daily mode after No, or if can't play Encore) */}
          {(
            step === ModalStep.SHARE ||
            (!isDailyMode || !canPlayEncore)
          ) && (
            <div className="flex flex-col items-center gap-2">
              {/* If coming from daily no-encore, or random, optionally intro */}
              {isEncoreMode && (
                <p className="text-sm text-muted-foreground text-center mb-2">
                  Share your Daily Fourdle results below.
                </p>
              )}
              {isMobile ? (
                <div className="grid grid-cols-2 gap-2 w-full">
                  <Button 
                    onClick={handleShare} 
                    className="flex items-center justify-center gap-2"
                    variant="outline"
                  >
                    <Copy size={18} />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button 
                    onClick={handleNativeShare} 
                    className="flex items-center justify-center gap-2"
                    variant="outline"
                  >
                    <SendHorizontal size={18} />
                    Share
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleShare} 
                  className="w-full flex items-center justify-center gap-2"
                  variant="outline"
                >
                  <Copy size={18} />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              )}
            </div>
          )}

          {/* Play Again / Random game button (always present) */}
          {(step === ModalStep.SHARE || (!isDailyMode || !canPlayEncore)) && (
            <div className="grid gap-2">
              <Button onClick={handlePlayAgain} className="w-full">
                {isSinglePlayMode ? "Play An Archive" : "Play Again"}
              </Button>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResultModal;
